/**
 * Matchmaker Quiz Analytics (BC-54)
 *
 * PURPOSE:
 * Consent-gated funnel events for the `matchmakerQuiz` Formie form, pushed
 * through the shared dataLayer helper (`pushEvent`). Two contexts, detected by
 * DOM markers and wired independently:
 *
 *   [data-quiz-form]   — the quiz itself (fullscreen quizPage OR the inline
 *                        `form` block). Emits `quiz_start` once, then
 *                        `quiz_step` on each Formie page change.
 *   [data-quiz-result] — the result page. Emits `quiz_complete` and
 *                        `quiz_result_view` with the server-resolved score band.
 *
 * NOT handled here:
 * - `generate_lead` — form-tracking.ts maps `matchmakerQuiz` and fires it on the
 *   final submit (reuses the shared contact-lead plumbing).
 * - Per-band CTA clicks — the result CTA carries `data-tag-*` attributes (the
 *   button atom's `ga` object) that GTM reads on click; no JS needed.
 *
 * See docs/analytics/matchmaker-quiz-events.md for the full event spec.
 *
 * Loaded globally via app.ts; both initialisers no-op unless their DOM marker
 * is present, so the cost on non-quiz pages is a single querySelector.
 */

import { pushEvent } from '~/js/utils/datalayer'

// -- Quiz funnel (quiz pages) ----------------------------------------------

/**
 * A page counts as a quiz "step" when it holds a question input. Welcome/intro
 * pages have none and are excluded — mirrors `numberSteps()` in matchmaker.ts.
 */
const STEP_SELECTOR = '.fui-radio, .fui-checkbox, .fui-input:not([type="hidden"])'

/** The ordered question pages within a form. */
const stepPages = (form: HTMLElement): HTMLElement[] =>
    Array.from(form.querySelectorAll<HTMLElement>('[data-fui-page]')).filter((page) =>
        page.querySelector(STEP_SELECTOR),
    )

/**
 * Resolve the page Formie is about to show from the toggle's `nextPageId`.
 *
 * `onFormiePageToggle` dispatches BEFORE Formie flips `data-fui-page-hidden`, so
 * the still-visible page is the OLD one — resolve the target by id instead
 * (pages carry it on `data-id`). Same approach as matchmaker.ts.
 */
const pageById = (form: HTMLElement, pageId: unknown): HTMLElement | null => {
    if (pageId === null || pageId === undefined || pageId === '') {
        return null
    }

    return form.querySelector<HTMLElement>(`[data-fui-page][data-id="${pageId}"]`)
}

/** The question text for a page — its heading/legend, trimmed and collapsed. */
const questionText = (page: HTMLElement): string => {
    const heading = page.querySelector<HTMLElement>(
        '.fui-heading, legend, .fui-legend, h1, h2, h3',
    )

    return (heading?.textContent ?? '').replace(/\s+/g, ' ').trim()
}

const initQuizFunnel = (form: HTMLElement): void => {
    const steps = stepPages(form)
    const totalSteps = steps.length
    let started = false

    form.addEventListener('onFormiePageToggle', (event) => {
        const nextPageId = (event as CustomEvent).detail?.data?.nextPageId
        const page = pageById(form, nextPageId)

        if (!page) {
            return
        }

        const stepIndex = steps.indexOf(page)

        // Non-question pages (welcome/intro) aren't part of the funnel.
        if (stepIndex === -1) {
            return
        }

        // First move onto a question page = the quiz has begun.
        if (!started) {
            started = true
            pushEvent('quiz_start')
        }

        pushEvent('quiz_step', {
            step: stepIndex + 1,
            total_steps: totalSteps,
            question: questionText(page),
        })
    })
}

// -- Result page -----------------------------------------------------------

/**
 * Fire the completion + result-view events. The band is resolved server-side
 * (the visitor never sees the raw score) and handed to JS via
 * `data-quiz-result` — so no score or PII reaches the dataLayer.
 */
const initQuizResult = (el: HTMLElement): void => {
    const band = el.dataset.quizResult?.trim()

    if (!band) {
        return
    }

    pushEvent('quiz_complete', { score_band: band })
    pushEvent('quiz_result_view', { score_band: band })
}

// -- Boot ------------------------------------------------------------------

const main = (): void => {
    document
        .querySelectorAll<HTMLElement>('[data-quiz-form]')
        .forEach((form) => initQuizFunnel(form))

    const result = document.querySelector<HTMLElement>('[data-quiz-result]')

    if (result) {
        initQuizResult(result)
    }
}

if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', main)
    } else {
        main()
    }
}
